import React, {
  ChangeEventHandler,
  ReactNode,
  useId,
} from 'react';
import {
  Box,
  InputLabel,
  TextField,
  Theme,
} from '@mui/material';
import {
  SxProps,
  SystemStyleObject,
} from '@mui/system';

import useTextField from '../../../hooks/useTextField';
import classes from '../CustomTextField/style';

interface ICustomControlledTextField {
  value: string | number;
  label?: string;
  name?: string;
  id?: string;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  maxLength?: number | null;
  select?: boolean;
  children?: ReactNode;
  customClass?: Record<string, unknown>;
  customTextFieldStyle?: SxProps<Theme>;
  usePreFilledColors?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const CustomControlledTextField = ({
  value,
  label = '',
  name = '',
  id,
  type = 'text',
  placeholder,
  disabled = false,
  error = false,
  helperText = ' ',
  required = false,
  fullWidth = true,
  multiline = false,
  rows = 3,
  maxLength = null,
  select = false,
  children = null,
  customClass = {},
  customTextFieldStyle = {},
  usePreFilledColors = false,
  onChange,
}: ICustomControlledTextField) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const { style } = useTextField({ value, usePreFilledColors });

  return (
    <Box component="div" sx={customClass as SystemStyleObject}>
      {label ? (
        <InputLabel
          htmlFor={inputId}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {label}
          {required ? (
            <Box component="span" sx={classes.customTextFieldRequireLabel}>
              *
            </Box>
          ) : null}
        </InputLabel>
      ) : null}
      <TextField
        id={inputId}
        name={name}
        type={type}
        placeholder={placeholder ?? label}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        helperText={helperText || ' '}
        inputProps={{ maxLength }}
        multiline={multiline}
        rows={rows}
        select={select}
        sx={[
          multiline ? classes.filledInput : {},
          { input: style },
          customTextFieldStyle,
        ] as SxProps}
        value={value}
        onChange={onChange}
      >
        {children}
      </TextField>
    </Box>
  );
};

export default CustomControlledTextField;
